import { useCallback, useEffect, useRef } from 'react';

const MIN_UID_LEN = 8;
const MAX_UID_LEN = 32;
/** Сброс буфера после паузы длиннее типичного ввода человека */
const BUFFER_RESET_MS = 220;
/** NFC-ридер печатает заметно быстрее человека (< ~40 ms между символами) */
const NFC_KEY_GAP_MS = 40;
/** Сколько подряд «быстрых» hex-символов нужно, чтобы считать ввод клином ридера */
const MIN_FAST_KEYS_IN_FIELD = 8;
const MIN_FAST_KEYS_GLOBAL = 2;

export function normalizeNfcUid(raw) {
  return String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-F]/gi, '');
}

function isHexKey(key) {
  return key.length === 1 && /[0-9A-Fa-f]/.test(key);
}

function isBindModeActive() {
  return !!document.querySelector('[data-nfc-reader-active="true"]');
}

export function isEditableTarget(target) {
  if (!target || !(target instanceof Element)) return false;
  const tag = target.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return !!target.closest('[contenteditable="true"], [contenteditable=""]');
}

/** Поля телефона / суммы / поиска — нельзя перехватывать как NFC-клин (цифры 0–9 = hex). */
export function isPhoneOrSearchField(target) {
  if (!target || !(target instanceof Element)) return false;
  if (target.closest('[data-no-nfc-intercept], [data-phone-input], [data-money-input]')) return true;
  if (!(target instanceof HTMLInputElement)) {
    return !!target.closest(
      'input[type="tel"], input[inputmode="tel"], input[type="number"], input[data-money-input]'
    );
  }
  const type = (target.type || '').toLowerCase();
  const mode = (target.inputMode || target.getAttribute('inputmode') || '').toLowerCase();
  const auto = (target.autocomplete || target.getAttribute('autocomplete') || '').toLowerCase();
  if (type === 'tel' || mode === 'tel' || mode === 'numeric' || mode === 'decimal') return true;
  if (type === 'number') return true;
  if (auto === 'tel' || auto === 'tel-national' || auto.startsWith('tel')) return true;
  if (type === 'search') return true;
  return false;
}

/**
 * Решает, перехватывать ли текущий hex-символ как NFC-клин.
 * Человек в поле ввода печатает медленнее ридера — не блокируем обычный ввод.
 */
export function shouldInterceptNfcWedge({
  inEditable,
  gapMs,
  fastKeyCount,
  bufferLength,
}) {
  const minFastKeys = inEditable ? MIN_FAST_KEYS_IN_FIELD : MIN_FAST_KEYS_GLOBAL;
  if (fastKeyCount >= minFastKeys) {
    return true;
  }

  if (inEditable && bufferLength > 0 && gapMs > NFC_KEY_GAP_MS) {
    return false;
  }

  return false;
}

/**
 * Обработка keydown в режиме привязки карты (NfcUidField «Считать»).
 * Весь ввод ридера идёт в буфер, символы не попадают в controlled Input (иначе теряется последняя цифра).
 */
export function handleBindModeKeyDown(e, { bufferRef, lastKeyRef, timeoutRef, flush, onScan, enabled }) {
  const now = Date.now();
  const gap = lastKeyRef.current > 0 ? now - lastKeyRef.current : BUFFER_RESET_MS + 1;
  if (gap > BUFFER_RESET_MS) {
    bufferRef.current = '';
  }
  lastKeyRef.current = now;

  if (e.key === 'Enter') {
    if (!bufferRef.current) return true;
    e.preventDefault();
    e.stopPropagation();
    const uid = normalizeNfcUid(bufferRef.current);
    bufferRef.current = '';
    if (uid.length >= MIN_UID_LEN && uid.length <= MAX_UID_LEN && enabled) {
      onScan?.(uid);
    }
    return true;
  }

  if (!isHexKey(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
    return true;
  }

  bufferRef.current += e.key;
  e.preventDefault();
  e.stopPropagation();
  clearTimeout(timeoutRef.current);
  timeoutRef.current = setTimeout(() => flush(enabled), BUFFER_RESET_MS + 40);
  return true;
}

/**
 * Считывание UID с R20D-USB (режим эмуляции клавиатуры: быстрый ввод + Enter).
 * Перехватывает «клин» ридера, чтобы UID не попадал в открытые поля формы.
 */
export function useNfcReader({ enabled = true, onScan, interceptWedge = false, bindMode = false } = {}) {
  const bufferRef = useRef('');
  const lastKeyRef = useRef(0);
  const fastKeyCountRef = useRef(0);
  const wedgeActiveRef = useRef(false);
  const timeoutRef = useRef(null);

  const flush = useCallback(
    (triggerScan) => {
      const uid = normalizeNfcUid(bufferRef.current);
      bufferRef.current = '';
      fastKeyCountRef.current = 0;
      wedgeActiveRef.current = false;
      if (uid.length >= MIN_UID_LEN && uid.length <= MAX_UID_LEN && triggerScan && enabled) {
        onScan?.(uid);
      }
    },
    [enabled, onScan]
  );

  useEffect(() => {
    if (!enabled && !interceptWedge) return undefined;

    const resetBuffer = () => {
      bufferRef.current = '';
      fastKeyCountRef.current = 0;
      wedgeActiveRef.current = false;
    };

    const onKeyDown = (e) => {
      if (isBindModeActive() && !bindMode) return;

      if (bindMode && enabled) {
        handleBindModeKeyDown(e, {
          bufferRef,
          lastKeyRef,
          timeoutRef,
          flush,
          onScan,
          enabled,
        });
        return;
      }

      const now = Date.now();
      const gap = lastKeyRef.current > 0 ? now - lastKeyRef.current : BUFFER_RESET_MS + 1;
      if (gap > BUFFER_RESET_MS) {
        resetBuffer();
      }
      lastKeyRef.current = now;

      if (e.key === 'Enter') {
        if (bufferRef.current) {
          const uid = normalizeNfcUid(bufferRef.current);
          if (uid.length >= MIN_UID_LEN && uid.length <= MAX_UID_LEN) {
            if (interceptWedge || wedgeActiveRef.current) {
              e.preventDefault();
              e.stopPropagation();
            }
            resetBuffer();
            if (enabled) {
              onScan?.(uid);
            }
          } else {
            resetBuffer();
          }
        }
        return;
      }

      if (!isHexKey(e.key) || e.ctrlKey || e.metaKey || e.altKey) {
        if (bufferRef.current !== '') {
          resetBuffer();
        }
        return;
      }

      const inEditable = isEditableTarget(e.target);
      const activeEl = document.activeElement;
      // Телефон / поиск: цифры пациента не должны уходить в NFC-буфер.
      if (
        (inEditable && isPhoneOrSearchField(e.target))
        || (activeEl instanceof Element && isPhoneOrSearchField(activeEl))
      ) {
        if (bufferRef.current !== '') {
          resetBuffer();
        }
        return;
      }

      const isFast = gap <= NFC_KEY_GAP_MS;

      if (inEditable && !isFast && bufferRef.current === '') {
        return;
      }

      if (inEditable && !isFast && bufferRef.current !== '') {
        resetBuffer();
        return;
      }

      bufferRef.current += e.key;
      if (isFast && bufferRef.current.length > 1) {
        fastKeyCountRef.current += 1;
      }

      wedgeActiveRef.current = shouldInterceptNfcWedge({
        inEditable,
        gapMs: gap,
        fastKeyCount: fastKeyCountRef.current,
        bufferLength: bufferRef.current.length,
      });

      if (interceptWedge && wedgeActiveRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => flush(enabled), BUFFER_RESET_MS + 40);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      clearTimeout(timeoutRef.current);
    };
  }, [enabled, interceptWedge, bindMode, flush, onScan]);

  return { normalizeNfcUid };
}
